import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Radio,
  Row,
  Skeleton,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  theme,
} from 'antd';
import { ReloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { authFetch } from '../../utils/api';
import { useViewScope } from '../../utils/viewScope';

const { Title, Text, Paragraph } = Typography;

/**
 * P1-3A — gate-reject 信心分析報表。
 *
 * 反偵測 gate 拒絕事件（connectivity_event.errorCode `vendor_reject:*`）
 * 依「信心分級」聚合，回答一個關鍵問題：這批 gate reject 到底是「真的偵測
 * 到逆向/篡改」還是「華為 / HONOR 等無 GMS 裝置的 ghost-map 誤判」。
 *
 * 三種信心分級（後端語意）：
 *   - high_confidence_tamper：真逆向（紅）——值得當真的 tamper 訊號
 *   - low_confidence_fp     ：誤判（綠）——no-GMS 裝置 ghost-map 假陽性，
 *                             代表該調 gate 閾值，而不是把它當真 tamper
 *   - ambiguous             ：無法判定（黃）——資料不足以分類
 *
 * 資料層 = 後端 `GET /api/admin/anti-detection-telemetry/gate-reject-confidence`
 * （#64 P1-3 Part A 聚合端點，commit 6a99dc2）。Brand 檢視軸沿用 authFetch
 * 的 X-Brand 機制（單品牌檢視送 header、聚合檢視省略），本區塊不另外處理。
 *
 * 時間窗用元件內 local state（不寫 URL query），避免與同頁 16-tile 儀表板
 * 既有的 `?window=` 參數互撞。
 */

type WindowKey = '7d' | '30d' | '90d';

const WINDOW_OPTIONS: { value: WindowKey; label: string; hours: number }[] = [
  { value: '7d', label: '近 7 天', hours: 168 },
  { value: '30d', label: '近 30 天', hours: 720 },
  { value: '90d', label: '近 90 天', hours: 2160 },
];

const DEFAULT_WINDOW: WindowKey = '30d';

type ConfidenceLevel =
  | 'high_confidence_tamper'
  | 'low_confidence_fp'
  | 'ambiguous';

interface ByConfidence {
  high_confidence_tamper: number;
  low_confidence_fp: number;
  ambiguous: number;
}

interface GateRejectConfidenceRow {
  reason: string;
  deviceClass: string;
  confidence: string;
  count: number;
  lastSeenAt: string;
}

interface GateRejectConfidenceData {
  brand: string | null;
  from: string;
  to: string;
  total: number;
  byConfidence: ByConfidence;
  rowCount: number;
  rows: GateRejectConfidenceRow[];
}

/** confidence 值 → 中文標籤 + AntD 預設 tag 顏色（暗色由 token 體系適配）。 */
const CONFIDENCE_META: Record<
  ConfidenceLevel,
  { label: string; color: string }
> = {
  high_confidence_tamper: { label: '真逆向', color: 'red' },
  low_confidence_fp: { label: '誤判', color: 'green' },
  ambiguous: { label: '無法判定', color: 'gold' },
};

function describeConfidence(value: string): { label: string; color: string } {
  return (
    CONFIDENCE_META[value as ConfidenceLevel] ?? {
      label: value || '—',
      color: 'default',
    }
  );
}

interface FetchState {
  loading: boolean;
  error: string | null;
  data: GateRejectConfidenceData | null;
  fetchedAt: string | null;
}

function useGateRejectConfidence(
  windowHours: number,
  brandForRequest: string | null
): FetchState & { refetch: () => Promise<void> } {
  const [state, setState] = useState<FetchState>({
    loading: true,
    error: null,
    data: null,
    fetchedAt: null,
  });

  // P2 race guard：切品牌 / 換時間窗會觸發新請求，舊請求若較晚回來
  // 不得覆蓋新請求結果。每次請求領一個遞增序號，回來時比對是否仍是最新。
  const reqIdRef = useRef(0);

  const fetchOnce = useCallback(async () => {
    const myReqId = ++reqIdRef.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const params = new URLSearchParams({ window: String(windowHours) });
      const res = await authFetch(
        `/api/admin/anti-detection-telemetry/gate-reject-confidence?${params.toString()}`
      );
      const result = await res.json();
      if (myReqId !== reqIdRef.current) return; // 已有更新請求，丟棄本次
      if (result.code === 0 && result.data) {
        setState({
          loading: false,
          error: null,
          data: result.data as GateRejectConfidenceData,
          fetchedAt: new Date().toISOString(),
        });
      } else {
        setState({
          loading: false,
          error: result.msg || '查詢失敗',
          data: null,
          fetchedAt: null,
        });
      }
    } catch (e) {
      if (myReqId !== reqIdRef.current) return; // 已有更新請求，丟棄本次
      setState({
        loading: false,
        error: e instanceof Error ? e.message : '查詢失敗',
        data: null,
        fetchedAt: null,
      });
    }
    // authFetch 依全域檢視範圍自動帶 X-Brand；把 brandForRequest 放進依賴，
    // 切品牌時立即重抓（修 P1b：切品牌報表不自動重整）。
  }, [windowHours, brandForRequest]);

  useEffect(() => {
    fetchOnce();
  }, [fetchOnce]);

  return { ...state, refetch: fetchOnce };
}

/**
 * gate-reject 信心分析區塊。掛在 16-tile 反偵測儀表板頁底部作為獨立 Card。
 */
export const GateRejectConfidence: React.FC = () => {
  const { token } = theme.useToken();
  const { brandForRequest } = useViewScope();
  const [windowKey, setWindowKey] = useState<WindowKey>(DEFAULT_WINDOW);
  const windowEntry =
    WINDOW_OPTIONS.find((o) => o.value === windowKey) ?? WINDOW_OPTIONS[1];

  const { loading, error, data, fetchedAt, refetch } = useGateRejectConfidence(
    windowEntry.hours,
    brandForRequest
  );

  const columns = useMemo<ColumnsType<GateRejectConfidenceRow>>(
    () => [
      {
        title: '拒絕原因 (reason)',
        dataIndex: 'reason',
        key: 'reason',
        render: (v: string) => <Text code>{v || '—'}</Text>,
      },
      {
        title: '裝置類別 (deviceClass)',
        dataIndex: 'deviceClass',
        key: 'deviceClass',
        render: (v: string) => <Text>{v || '—'}</Text>,
      },
      {
        title: '信心分級',
        dataIndex: 'confidence',
        key: 'confidence',
        render: (v: string) => {
          const meta = describeConfidence(v);
          return (
            <Tag color={meta.color} data-testid={`confidence-tag-${v}`}>
              {meta.label}
            </Tag>
          );
        },
      },
      {
        title: '事件數',
        dataIndex: 'count',
        key: 'count',
        align: 'right',
        render: (v: number) => <Text strong>{v}</Text>,
      },
      {
        title: '最後發生',
        dataIndex: 'lastSeenAt',
        key: 'lastSeenAt',
        render: (v: string) =>
          v ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {new Date(v).toLocaleString()}
            </Text>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
    ],
    []
  );

  const byConfidence: ByConfidence = data?.byConfidence ?? {
    high_confidence_tamper: 0,
    low_confidence_fp: 0,
    ambiguous: 0,
  };
  const total = data?.total ?? 0;

  return (
    <Card size="small" data-testid="gate-reject-confidence">
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Space align="center" wrap>
          <SafetyCertificateOutlined style={{ fontSize: 20 }} />
          <Title level={4} style={{ margin: 0 }}>
            gate-reject 信心分析
          </Title>
          <Tag color="purple">P1-3A</Tag>
        </Space>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          反偵測 gate 拒絕事件依「信心分級」拆解，快速分辨哪些是{' '}
          <Text strong>真逆向</Text>、哪些是華為 / HONOR 等無 GMS 裝置的{' '}
          <Text strong>誤判</Text>。誤判占比高 = 該調 gate 閾值，而不是把它當真
          tamper 處理。
        </Paragraph>

        <Card size="small" data-testid="gate-reject-filters">
          <Space size={16} wrap>
            <Space size={8}>
              <Text strong>時間視窗</Text>
              <Radio.Group
                value={windowKey}
                onChange={(e) => setWindowKey(e.target.value as WindowKey)}
                buttonStyle="solid"
                size="small"
              >
                {WINDOW_OPTIONS.map((opt) => (
                  <Radio.Button key={opt.value} value={opt.value}>
                    {opt.label}
                  </Radio.Button>
                ))}
              </Radio.Group>
            </Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                refetch();
              }}
              size="small"
            >
              重新整理
            </Button>
            {fetchedAt && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                最後更新 {new Date(fetchedAt).toLocaleTimeString()}
              </Text>
            )}
            {data && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Brand <Tag color="default">{data.brand || '全品牌'}</Tag>共{' '}
                <strong>{data.rowCount}</strong> 種組合
              </Text>
            )}
          </Space>
        </Card>

        {error && (
          <Alert
            type="error"
            message="查詢失敗"
            description={error}
            showIcon
            closable
          />
        )}

        {loading && !data ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : (
          <>
            <Row gutter={[12, 12]} data-testid="gate-reject-cards">
              <Col xs={24} sm={8}>
                <Card size="small">
                  <Statistic
                    title="真逆向"
                    value={byConfidence.high_confidence_tamper}
                    valueStyle={{ color: token.colorError }}
                  />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    high_confidence_tamper
                  </Text>
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card size="small">
                  <Statistic
                    title="誤判"
                    value={byConfidence.low_confidence_fp}
                    valueStyle={{ color: token.colorSuccess }}
                  />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    low_confidence_fp（no-GMS ghost-map）
                  </Text>
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card size="small">
                  <Statistic
                    title="無法判定"
                    value={byConfidence.ambiguous}
                    valueStyle={{ color: token.colorWarning }}
                  />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    ambiguous
                  </Text>
                </Card>
              </Col>
            </Row>

            <Text type="secondary" style={{ fontSize: 12 }}>
              視窗內 gate-reject 事件合計 <strong>{total}</strong> 筆
            </Text>

            <Table<GateRejectConfidenceRow>
              size="small"
              rowKey={(r) => `${r.reason}|${r.deviceClass}|${r.confidence}`}
              columns={columns}
              dataSource={data?.rows ?? []}
              pagination={false}
              scroll={{ x: 'max-content' }}
              data-testid="gate-reject-table"
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="視窗內無 gate-reject 事件"
                  />
                ),
              }}
            />
          </>
        )}
      </Space>
    </Card>
  );
};

export default GateRejectConfidence;
